import React, { useEffect, useState } from 'react'
import AdsDisclaimerTooltip from '@/components/AdsDisclaimerTooltip/AdsDisclaimerTooltip'
import { ArrowRight } from 'react-bootstrap-icons'
import HorizontalScroll from '@/components/atoms/HorizontalScroll/HorizontalScroll'
import HorizontalScrollLoader from '@/components/atoms/HorizontalScroll/HorizontalScrollLoader'
import NewsItem from '@/components/molecules/NewsItem/NewsItem'
import ChipsMenu from '@/components/organisms/ChipsMenu'
import { fetchFeaturedCenters } from '@/repository/center/fetchFeaturedCenter'
import { isLocationInUnitedStates } from '@/domain/location/useCases/isLocationInUnitedStates'
import { getThirdPartyReviews } from '@/domain/center'
import { Location } from '@/types/models/Location.model'
import { sortFeaturedCentersByScore } from '@/domain/useCases/score/sortFeaturedCentersByScore'
import { getDestinationOptions } from '@/domain/destinations/useCases/getPopularDestinationOptions'
import { generateCustomizedTermData } from '@/domain/destinations/useCases/generateCustomizedTermData'
import SegmentCardTracker from '../CenterCards/SegmentCardTracker'
import { ZONE_IDS_LIST } from '@/domain/segment/constants'
import useSegment from '@/hooks/segment/useSegment'
import { PageType } from '@/types/models/PageType.model'

const DEFAULT_RESULTS_PER_PAGE = 10

const getFeaturedCenters = async (
  location,
  termData,
  searchState,
  pageType
) => {
  const featuredCenters = await fetchFeaturedCenters({
    location,
    hitsPerPage: DEFAULT_RESULTS_PER_PAGE,
    termData
  })
  const scoredFeaturedCenters = sortFeaturedCentersByScore({
    featuredCenters,
    searchState,
    locationType: location?.type,
    pageType
  })
  return scoredFeaturedCenters
}

const ExplorePopularDestinations = ({
  location,
  searchState,
  pageType,
  feedPosition,
  refinements
}: {
  location: Location
  searchState?
  pageType?: PageType
  feedPosition
  refinements
}): JSX.Element => {
  const analytics = useSegment()

  const [selectedOptionId, setSelectedOptionId] =
    useState<string>('featured-us')
  const [isLoading, setIsLoading] = useState(false)
  const [fetchedCenters, setFetchedCenters] = useState({})

  const { US_DESTINATION_OPTIONS, NON_US_DESTINATION_OPTIONS } =
    getDestinationOptions((id) => {
      setSelectedOptionId(id)
      analytics.exploreDestination.tabToggle({ tab: id as string })
    }, generateCustomizedTermData(location))

  const destinationOptions = isLocationInUnitedStates(location)
    ? US_DESTINATION_OPTIONS
    : NON_US_DESTINATION_OPTIONS

  const getSelectedOptionById = (id) => {
    return destinationOptions.find((option) => option.id === id)
  }

  const loadCenters = async (optionId) => {
    if (!fetchedCenters[optionId]) {
      const option = getSelectedOptionById(optionId)
      setIsLoading(true)
      const featuredCenters = await getFeaturedCenters(
        option?.location,
        option?.termData,
        searchState,
        pageType
      )
      setFetchedCenters((prevData) => ({
        ...prevData,
        [optionId]: featuredCenters
      }))
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!destinationOptions.some((option) => option.id === selectedOptionId)) {
      setSelectedOptionId(destinationOptions[0].id)
    }
  }, [destinationOptions])

  useEffect(() => {
    loadCenters(selectedOptionId)
  }, [selectedOptionId, loadCenters])

  return (
    <div className="-mx-4 my-8 flex flex-col gap-4 border border-seafoam-50 bg-seafoam-25 p-4 md:mx-0 md:rounded-[20px] md:p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-FieldsVariable text-xl font-medium leading-7 text-seafoam-900">
          Explore Popular Destinations
        </h2>

        <AdsDisclaimerTooltip />
      </div>

      <div className="hide-scroll -mx-4 flex items-start gap-2 overflow-scroll p-1 px-4">
        <ChipsMenu
          menuClassName="flex items-center gap-2 animate-[slideInListSelectionButtonGroup_0.150s_ease-in-out] rounded-full bg-white p-1 border border-gray-300;"
          optionClassName="text-gray-800"
          optionActiveClassName="!text-seafoam-900"
          optionBackgroundClassName="!bg-seafoam-100"
          options={destinationOptions}
        />
      </div>

      <div>
        {destinationOptions.map(
          (option) =>
            option.id === selectedOptionId && (
              <div key={option.id}>
                <HorizontalScroll
                  key={option.id}
                  id={`featured-destinations-${option.id}`}
                  alwaysScroll
                  count={3}
                  minWidth={'400px'}
                  maxWidth="30vw"
                  gridClassName="h-[323px] max-h-[323px] lg:h-[343px] lg:max-h-[343px]"
                  marginBottom="0px"
                  withGradient={false}
                  scrollOverPadding
                  removeArrowBorderOffset
                >
                  {isLoading ? (
                    <HorizontalScrollLoader />
                  ) : (
                    fetchedCenters[selectedOptionId]?.map((center, i) => (
                      <SegmentCardTracker
                        center={center}
                        position={feedPosition}
                        positionInElement={i + 1}
                        zone={ZONE_IDS_LIST.Explore}
                        key={center.slug}
                        refinements={refinements}
                      >
                        <NewsItem
                          key={i}
                          center={center}
                          className="max-w-[416px]"
                          fluid
                          image={
                            center.featured_image
                              ? `https://res.cloudinary.com/rehabpath/image/upload/e_gen_restore/c_fill,w_640,h_400,q_15/f_auto/${center.featured_image
                                  .split('/')
                                  .pop()}`
                              : `https://res.cloudinary.com/rehabpath/image/upload/e_gen_restore/c_fill,w_640,h_400,q_15/f_auto/${center.slug}-thumb`
                          }
                          title={center.title}
                          noReadMore
                          sponsor={false}
                          subtitle={center.location_label}
                          link={`/${center.slug}/`}
                          url={`${center.slug}`}
                          hasCertifications={Boolean(
                            center.certifications?.length
                          )}
                          reviewCount={center.review_count}
                          reviewAverage={center.review_average}
                          thirdPartyReviews={getThirdPartyReviews(center)}
                          price={center.pricing_label}
                          hideDescription
                        />
                      </SegmentCardTracker>
                    ))
                  )}
                </HorizontalScroll>
              </div>
            )
        )}
      </div>
      <div className="flex h-10 justify-start">
        <a
          href={getSelectedOptionById(selectedOptionId)?.linkUrl}
          rel={getSelectedOptionById(selectedOptionId)?.linkRel}
          className="flex items-center justify-center gap-1"
          onClick={() => {
            analytics.exploreDestination.linkClick()
          }}
        >
          <span className="font-medium text-seafoam-900 underline">
            {getSelectedOptionById(selectedOptionId)?.linkText}
          </span>
          <ArrowRight size="16" />
        </a>
      </div>
    </div>
  )
}

export default ExplorePopularDestinations
